<template>
  <div class="subscriptions-container">
    <!-- Header -->
    <div class="subscriptions-header">
      <h3 class="subscriptions-title">{{ $t('subscription.title') }}</h3>
      <p class="subscriptions-subtitle">{{ $t('subscription.subtitle') }}</p>
    </div>

    <!-- Credit Auto Top-up Settings -->
    <div class="credit-topup-section mt-4">
      <div class="credit-topup-header">
        <h4 class="credit-topup-title">
          <b-icon icon="coin" class="mr-2"></b-icon>
          {{ $t('creditTopup.title') }}
        </h4>
        <p class="credit-topup-subtitle">{{ $t('creditTopup.subtitle') }}</p>
      </div>

      <div class="credit-topup-card" v-if="!loadingCreditSettings">
        <div class="form-group d-flex align-items-center">
          <b-form-checkbox
            v-model="creditSettings.auto_topup_enabled"
            switch
            class="mr-3"
          >
            {{ $t('creditTopup.enableAutoTopup') }}
          </b-form-checkbox>
        </div>

        <div v-if="creditSettings.auto_topup_enabled">
          <div class="form-group">
            <label>{{ $t('creditTopup.thresholdLabel') }}</label>
            <b-input-group :prepend="billingCurrency">
              <b-form-input
                v-model.number="creditSettings.auto_topup_threshold"
                type="number"
                min="0"
                step="0.01"
                :placeholder="$t('creditTopup.thresholdPlaceholder')"
              ></b-form-input>
            </b-input-group>
            <small class="form-text text-muted">{{ $t('creditTopup.thresholdHint') }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t('creditTopup.amountLabel') }}</label>
            <b-input-group :prepend="billingCurrency">
              <b-form-input
                v-model.number="creditSettings.auto_topup_amount"
                type="number"
                min="0"
                step="0.01"
                :placeholder="$t('creditTopup.amountPlaceholder')"
              ></b-form-input>
            </b-input-group>
            <small class="form-text text-muted">{{ $t('creditTopup.amountHint') }}</small>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="primary"
            @click="saveCreditSettings"
            :disabled="savingCreditSettings"
          >
            <b-spinner v-if="savingCreditSettings" small class="mr-2"></b-spinner>
            {{ $t('creditTopup.save') }}
          </b-button>
        </div>
      </div>

      <div v-else class="d-flex justify-content-center py-4">
        <b-spinner variant="primary"></b-spinner>
      </div>
    </div>

    <div class="manual-topup-section mt-4 mb-4">
      <div class="credit-topup-header">
        <h4 class="credit-topup-title">
          <b-icon icon="plus-circle" class="mr-2"></b-icon>
          {{ $t('creditTopup.manualTitle') }}
        </h4>
        <p class="credit-topup-subtitle">{{ $t('creditTopup.manualSubtitle') }}</p>
      </div>

      <div class="credit-topup-card">
        <div class="manual-topup-presets mb-3">
          <b-button
            v-for="preset in manualTopupPresets"
            :key="preset"
            :variant="manualTopupAmount === preset && !manualTopupCustom ? 'primary' : 'outline-secondary'"
            size="sm"
            class="mr-2 mb-2"
            @click="selectTopupPreset(preset)"
          >
            {{ formatPreset(preset) }}
          </b-button>
          <b-button
            :variant="manualTopupCustom ? 'primary' : 'outline-secondary'"
            size="sm"
            class="mr-2 mb-2"
            @click="manualTopupCustom = true"
          >
            {{ $t('creditTopup.manualCustom') }}
          </b-button>
        </div>

        <div class="form-group" v-if="manualTopupCustom">
          <label>{{ $t('creditTopup.manualAmountLabel') }} ({{ billingCurrency }})</label>
          <b-input-group :prepend="billingCurrency">
            <b-form-input
              v-model.number="manualTopupCustomAmount"
              type="number"
              min="1"
              step="1"
              :placeholder="$t('creditTopup.manualCustomPlaceholder')"
            ></b-form-input>
          </b-input-group>
        </div>

        <div class="d-flex align-items-center justify-content-between mt-3">
          <span class="text-muted" style="font-size:0.85rem" v-if="manualTopupEffectiveAmount > 0">
            {{ $t('creditTopup.manualAddingSummary', { pay: formatMoney(manualTopupEffectiveAmount), receive: formatMoney(manualTopupEffectiveAmount * (1 + topupBonus / 100)), bonus: topupBonus }) }}
          </span>
          <span v-else></span>
          <b-button
            variant="success"
            @click="doManualTopup"
            :disabled="doingManualTopup || manualTopupEffectiveAmount <= 0"
          >
            <b-spinner v-if="doingManualTopup" small class="mr-2"></b-spinner>
            {{ $t('creditTopup.manualConfirm') }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="!subscriptions" class="loading-state">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <!-- Empty State -->
    <div v-else-if="subscriptions.length === 0" class="empty-state">
      <div class="empty-icon">
        <b-icon icon="credit-card" font-scale="3"></b-icon>
      </div>
      <h4>{{ $t('subscription.noSubscriptions') }}</h4>
      <p>{{ $t('subscription.noSubscriptionsDesc') }}</p>
      <b-button variant="primary" to="/admin/marketplace">
        <b-icon icon="shop" class="mr-2"></b-icon>
        {{ $t('exploreMarketplace') }}
      </b-button>
    </div>

    <!-- Subscriptions List -->
    <div v-else class="subscriptions-list">
      <!-- Expired Subscriptions Alert -->
      <div v-if="expiredSubscriptions.length > 0" class="alert alert-warning expired-alert">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
            <strong>{{ expiredSubscriptions.length }} {{ expiredSubscriptions.length === 1 ? $t('subscription.expiredSubscription') : $t('subscription.expiredSubscriptions') }}</strong>
          </div>
          <small>{{ $t('subscription.renewToReactivate') }}</small>
        </div>
      </div>

      <div 
        v-for="(group, refNo) in groupedSubscriptions" 
        :key="refNo"
        class="subscription-group"
      >
        <!-- Group Header (shown when multiple items in group) -->
        <div v-if="group.length > 1" class="group-header">
          <div class="group-title-section">
            <div class="group-title">
              <b-icon icon="collection" class="mr-2"></b-icon>
              <span>{{ getGroupTitle(group) }}</span>
            </div>
            <div class="group-price">
              <span class="currency">{{ group[0].currency === 'USD' ? '$' : '' }}</span>
              <span class="amount">{{ getGroupTotalPrice(group) }}</span>
              <span class="currency-suffix" v-if="group[0].currency !== 'USD'">{{ group[0].currency }}</span>
              <span class="period">/{{ group[0].period === 12 ? $t('subscription.year') : $t('subscription.month') }}</span>
            </div>
          </div>
          <div v-if="getGroupLicenseCode(group)" class="group-license">
            <span class="license-label-inline">{{ $t('subscription.licenseCode') }}:</span>
            <code>{{ getGroupLicenseCode(group) }}</code>
            <b-button 
              variant="link" 
              size="sm" 
              @click="copyLicenseCode(getGroupLicenseCode(group))"
              class="copy-btn-inline"
            >
              <b-icon icon="clipboard"></b-icon>
            </b-button>
          </div>
          <div v-if="getGroupBundleProducts(group).length > 0" class="group-includes">
            <span class="includes-label">{{ $t('subscription.includes') }}:</span>
            <div class="includes-list">
              <b-badge 
                v-for="product in getGroupBundleProducts(group)" 
                :key="product.id"
                :variant="getProductBadgeVariant(product.group)"
                class="mr-1 mb-1"
              >
                <b-icon :icon="getProductIcon(product.group)" class="mr-1"></b-icon>
                {{ product.name }}
              </b-badge>
            </div>
          </div>
          <!-- Actions for entire bundle -->
          <div v-if="group.length > 1" class="group-actions">
            <b-button 
              v-if="canCancel(getGroupLicenseCode(group) ? group.find(s => s.license_code) : group[0])" 
              variant="outline-danger" 
              size="sm"
              @click="cancel(getGroupLicenseCode(group) ? group.find(s => s.license_code).uuid : group[0].uuid)"
            >
              <b-icon icon="x-circle" class="mr-1"></b-icon>
              {{ $t('subscription.cancel') }}
            </b-button>
            <b-button 
              v-if="canRenew(getGroupLicenseCode(group) ? group.find(s => s.license_code) : group[0])" 
              variant="success" 
              size="sm"
              @click="renew(getGroupLicenseCode(group) ? group.find(s => s.license_code).uuid : group[0].uuid)"
            >
              <b-icon icon="arrow-repeat" class="mr-1"></b-icon>
              {{ $t('subscription.renew') }}
            </b-button>
            <b-button 
              v-if="isExpired(getGroupLicenseCode(group) ? group.find(s => s.license_code) : group[0])" 
              variant="danger" 
              size="sm"
              @click="remove(getGroupLicenseCode(group) ? group.find(s => s.license_code).uuid : group[0].uuid)"
            >
              <b-icon icon="trash" class="mr-1"></b-icon>
              {{ $t('subscription.remove') }}
            </b-button>
          </div>
        </div>

        <!-- Individual Subscription Cards in Group -->
        <div 
          v-for="subscription in group" 
          :key="subscription.uuid"
          class="subscription-card"
          :class="{ 
            'expired-subscription': subscription.is_expired,
            'in-group': group.length > 1
          }"
        >
          <!-- Card Header -->
          <div class="subscription-header">
            <div class="product-info">
              <div class="product-icon">
                <b-icon :icon="getProductIcon(subscription.license_product.group)" font-scale="1.5"></b-icon>
              </div>
              <div class="product-details">
                <h4 class="product-name">
                  {{ subscription.license_product.name }}
                  <b-badge v-if="isInTrialPeriod(subscription)" variant="info" class="trial-badge-inline ml-2">
                    <b-icon icon="clock-history" class="mr-1"></b-icon>
                    {{ $t('subscription.trialPeriod') }} ({{ getRemainingTrialDays(subscription) }}d)
                  </b-badge>
                </h4>
                <span class="product-type">{{ subscription.license_product.group }}</span>
              </div>
            </div>
            <div class="subscription-status">
              <b-badge :variant="getStatusVariant(subscription)" class="status-badge">
                {{ getStatusText(subscription) }}
              </b-badge>
            </div>
          </div>

          <!-- Card Body -->
          <div class="subscription-body">
            <!-- Price Section -->
            <div class="price-section">
              <div class="price-amount">
                <span class="currency">{{ subscription.currency === 'USD' ? '$' : '' }}</span>
                <span class="amount">{{ subscription.human_price }}</span>
                <span class="currency-suffix" v-if="subscription.currency !== 'USD'">{{ subscription.currency }}</span>
                <span class="period">/{{ subscription.period === 12 ? $t('subscription.year') : $t('subscription.month') }}</span>
              </div>
              <div class="billing-info" v-if="subscription.period">
                {{ subscription.period === 12 ? $t('subscription.billedAnnually') : $t('subscription.billedMonthly') }}
              </div>
            </div>

            <!-- Details Grid -->
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">{{ $t('subscription.paymentMethod') }}</span>
                <span class="detail-value">
                  <b-icon :icon="getPaymentIcon(subscription.payment_method)" class="mr-1"></b-icon>
                  {{ formatPaymentMethod(subscription.payment_method) }}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('subscription.paymentStatus') }}</span>
                <span class="detail-value">
                  <b-badge :variant="getPaymentStatusVariant(subscription.payment_status)" pill>
                    {{ formatPaymentStatus(subscription.payment_status) }}
                  </b-badge>
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('subscription.renewedAt') }}</span>
                <span class="detail-value">{{ formatDate(subscription.renewed_at) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('subscription.expiresAt') }}</span>
                <span class="detail-value" :class="{ 'expiring-soon': isExpiringSoon(subscription) }">
                  {{ formatDate(subscription.expires_at) }}
                  <b-icon v-if="isExpiringSoon(subscription)" icon="exclamation-triangle" class="ml-1"></b-icon>
                </span>
              </div>
            </div>

            <!-- License Code (only show if not in a group) -->
            <div v-if="group.length === 1" class="license-section">
              <span class="license-label">{{ $t('subscription.licenseCode') }}</span>
              <div class="license-code">
                <code>{{ subscription.license_code }}</code>
                <b-button 
                  variant="link" 
                  size="sm" 
                  @click="copyLicenseCode(subscription.license_code)"
                  class="copy-btn"
                >
                  <b-icon icon="clipboard"></b-icon>
                </b-button>
              </div>
            </div>
          </div>

          <!-- Card Footer -->
          <div class="subscription-footer" v-if="group.length === 1">
            <!-- Only show buttons for individual subscriptions (not in a group) -->
            <b-button 
              v-if="canCancel(subscription)" 
              variant="outline-danger" 
              size="sm"
              @click="cancel(subscription.uuid)"
            >
              <b-icon icon="x-circle" class="mr-1"></b-icon>
              {{ $t('subscription.cancel') }}
            </b-button>
            <b-button 
              v-if="canRenew(subscription)" 
              variant="success" 
              size="sm"
              @click="renew(subscription.uuid)"
            >
              <b-icon icon="arrow-repeat" class="mr-1"></b-icon>
              {{ $t('subscription.renew') }}
            </b-button>
            <b-button 
              v-if="isExpired(subscription)" 
              variant="danger" 
              size="sm"
              @click="remove(subscription.uuid)"
            >
              <b-icon icon="trash" class="mr-1"></b-icon>
              {{ $t('subscription.remove') }}
            </b-button>
          </div>

          <!-- Trial/Lifetime Badge -->
          <div class="special-badge" v-if="subscription.is_trial || subscription.is_lifetime">
            <b-badge variant="info" v-if="subscription.is_trial">
              <b-icon icon="clock" class="mr-1"></b-icon>
              {{ $t('trial') }}
            </b-badge>
            <b-badge variant="warning" v-if="subscription.is_lifetime">
              <b-icon icon="infinity" class="mr-1"></b-icon>
              {{ $t('subscription.lifetime') }}
            </b-badge>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Section -->
    <div v-if="subscriptions && subscriptions.length > 0" class="summary-section">
      <div class="summary-card">
        <div class="summary-item">
          <span class="summary-label">{{ $t('subscription.totalSubscriptions') }}</span>
          <span class="summary-value">{{ subscriptions.length }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">{{ $t('subscription.monthlyTotal') }}</span>
          <span class="summary-value highlight">{{ formatTotalPrice() }}</span>
        </div>
      </div>
    </div>

    <!-- Cancel Subscription Modal -->
    <main-dialog 
      v-model="cancelModal.show" 
      centered 
      hide-footer
      hide-header
      modal-class="cancel-modal"
      content-class="cancel-modal-content"
    >
      <div class="cancel-modal-body">
        <div class="sad-emoji">😢</div>
        <h3 class="cancel-title">
          {{ cancelModal.isTrialCancellation ? $t('subscription.cancelModal.trialTitle') : $t('subscription.cancelModal.title') }}
        </h3>
        <p class="cancel-message">
          {{ cancelModal.isTrialCancellation ? $t('subscription.cancelModal.trialMessage') : $t('subscription.cancelModal.message') }}
        </p>

        <!-- Trial Period Warning -->
        <div v-if="cancelModal.isTrialCancellation" class="trial-warning">
          <div class="warning-icon">
            <b-icon icon="exclamation-triangle-fill" font-scale="2"></b-icon>
          </div>
          <div class="warning-content">
            <h5>{{ $t('subscription.cancelModal.trialWarningTitle') }}</h5>
            <ul>
              <li>{{ $t('subscription.cancelModal.trialWarning1') }}</li>
              <li>{{ $t('subscription.cancelModal.trialWarning2') }}</li>
            </ul>
          </div>
        </div>

        <p class="cancel-note" v-if="!cancelModal.isTrialCancellation">{{ $t('subscription.cancelModal.note') }}</p>

        <div class="cancel-actions">
          <b-button 
            variant="primary" 
            block 
            @click="cancelModal.show = false"
            class="keep-btn"
          >
            <b-icon icon="heart-fill" class="mr-2"></b-icon>
            {{ $t('subscription.cancelModal.keepSubscription') }}
          </b-button>
          <b-button 
            variant="link" 
            block 
            @click="confirmCancel"
            class="cancel-btn"
            :disabled="cancelModal.loading"
          >
            <b-spinner v-if="cancelModal.loading" small class="mr-2"></b-spinner>
            {{ cancelModal.isTrialCancellation ? $t('subscription.cancelModal.confirmTrialCancel') : $t('subscription.cancelModal.confirmCancel') }}
          </b-button>
        </div>
      </div>
    </main-dialog>

  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.subscriptions-container {
  padding: 24px;
}

.subscriptions-header {
  margin-bottom: 32px;

  .subscriptions-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .subscriptions-subtitle {
    color: var(--text-secondary);
    font-size: 1rem;
    margin: 0;
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 16px;

  .empty-icon {
    color: var(--text-muted);
    margin-bottom: 24px;
  }

  h4 {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 8px;
  }

  p {
    color: var(--text-secondary);
    margin-bottom: 24px;
  }
}

.subscriptions-list {
  display: grid;
  gap: 20px;
}

.subscription-group {
  display: flex;
  flex-direction: column;
  background: rgba(102, 126, 234, 0.03);
  border: 1px solid rgba(102, 126, 234, 0.15);
  border-radius: 16px;
  padding: 4px;
}

.group-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(102, 126, 234, 0.08) 100%);
  border: 1px solid rgba(102, 126, 234, 0.4);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .group-title-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .group-title {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    font-weight: 600;
    color: $protokol-royal-active;
  }

  .group-price {
    display: flex;
    align-items: baseline;
    gap: 4px;

    .currency {
      font-size: 1rem;
      color: var(--text-primary);
      font-weight: 600;
    }

    .amount {
      font-size: 1.5rem;
      font-weight: 700;
      color: $protokol-royal-active;
    }

    .currency-suffix {
      font-size: 0.9rem;
      color: var(--text-secondary);
    }

    .period {
      font-size: 0.85rem;
      color: var(--text-secondary);
    }
  }

  .group-license {
    display: flex;
    align-items: center;
    gap: 8px;

    .license-label-inline {
      font-size: 0.8rem;
      color: var(--text-secondary);
    }

    code {
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 0.85rem;
      color: $protokol-royal-active;
      background: transparent;
      padding: 4px 8px;
      border-radius: 4px;
      background: rgba(102, 126, 234, 0.15);
    }

    .copy-btn-inline {
      color: var(--text-muted);
      padding: 2px 6px;

      &:hover {
        color: var(--text-primary);
      }
    }
  }

  .group-includes {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(102, 126, 234, 0.2);

    .includes-label {
      font-size: 0.8rem;
      color: var(--text-secondary);
      font-weight: 600;
    }

    .includes-list {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
  }

  .group-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid rgba(102, 126, 234, 0.2);
  }
}

.expired-alert {
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  color: #f59e0b;
  margin-bottom: 8px;

  strong {
    font-size: 1rem;
  }

  small {
    color: rgba(245, 158, 11, 0.8);
  }
}

.subscription-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &.expired-subscription {
    border-color: rgba(245, 158, 11, 0.4);
    background: rgba(245, 158, 11, 0.05);

    &:hover {
      border-color: rgba(245, 158, 11, 0.6);
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
    }
  }

  &.in-group {
    border-left: 4px solid rgba(102, 126, 234, 0.6);
    border-radius: 12px;
    margin-left: 12px;
    
    &:not(:last-child) {
      margin-bottom: 8px;
    }
    
    &:first-of-type {
      margin-top: 8px;
    }
  }
}

.subscription-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid $dark-border2;
  background: $dark-hard2-background;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.product-icon {
  width: 48px;
  height: 48px;
  background: rgba(102, 126, 234, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $protokol-royal-active;
}

.product-details {
  .product-name {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 4px 0;
  }

  .product-type {
    font-size: 0.85rem;
    color: var(--text-muted);
  }
}

.status-badge {
  font-size: 0.8rem;
  padding: 6px 12px;
  font-weight: 500;
}

.subscription-body {
  padding: 24px;
}

.price-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid $dark-border2;

  .price-amount {
    display: flex;
    align-items: baseline;
    gap: 2px;

    .currency {
      font-size: 1.25rem;
      color: var(--text-secondary);
    }

    .amount {
      font-size: 2.5rem;
      font-weight: 700;
      color: var(--text-primary);
    }

    .currency-suffix {
      font-size: 1rem;
      color: var(--text-secondary);
      margin-left: 4px;
    }

    .period {
      font-size: 1rem;
      color: var(--text-muted);
      margin-left: 4px;
    }
  }

  .billing-info {
    font-size: 0.85rem;
    color: var(--text-muted);
    margin-top: 4px;
  }
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .detail-label {
    font-size: 0.8rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .detail-value {
    font-size: 0.95rem;
    color: var(--text-primary);
    display: flex;
    align-items: center;

    &.expiring-soon {
      color: #f59e0b;
    }
  }
}

.license-section {
  background: $dark-hard2-background;
  border-radius: 8px;
  padding: 12px 16px;

  .license-label {
    font-size: 0.75rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 6px;
  }

  .license-code {
    display: flex;
    align-items: center;
    justify-content: space-between;

    code {
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 0.9rem;
      color: $protokol-royal-active;
      background: transparent;
    }

    .copy-btn {
      color: var(--text-muted);
      padding: 4px 8px;

      &:hover {
        color: var(--text-primary);
      }
    }
  }
}

.subscription-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid $dark-border2;
  background: $dark-hard2-background;
}

.trial-badge-inline {
  font-size: 0.7rem;
  padding: 4px 8px;
  font-weight: 500;
  vertical-align: middle;
}

.count-badge {
  font-size: 0.7rem;
  padding: 4px 8px;
  font-weight: 500;
  vertical-align: middle;
}

.special-badge {
  position: absolute;
  top: 16px;
  right: 16px;
}

.summary-section {
  margin-top: 32px;
}

.summary-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .summary-label {
    font-size: 0.85rem;
    color: var(--text-muted);
  }

  .summary-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);

    &.highlight {
      color: $protokol-royal-active;
    }
  }
}

// Cancel Modal Styles
::v-deep .cancel-modal {
  .modal-dialog {
    max-width: 420px;
  }
}

::v-deep .cancel-modal-content {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 20px;
  overflow: hidden;
}

.cancel-modal-body {
  padding: 40px 32px;
  text-align: center;

  .sad-emoji {
    font-size: 4rem;
    margin-bottom: 20px;
    animation: shake 0.5s ease-in-out;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px) rotate(-5deg); }
    75% { transform: translateX(5px) rotate(5deg); }
  }

  .cancel-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
  }

  .cancel-message {
    color: var(--text-secondary);
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .cancel-features {
    background: $dark-hard2-background;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
    text-align: left;

    .feature-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;
      color: var(--text-primary);
      font-size: 0.95rem;

      .feature-icon {
        font-size: 1.1rem;
        
        &.lost {
          color: #ef4444;
        }
      }
    }
  }

  .trial-warning {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    display: flex;
    gap: 16px;

    .warning-icon {
      color: #ef4444;
      flex-shrink: 0;
    }

    .warning-content {
      flex: 1;
      text-align: left;

      h5 {
        color: var(--text-primary);
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 12px;
      }

      ul {
        margin: 0;
        padding-left: 20px;
        color: var(--text-primary);
        font-size: 0.9rem;
        line-height: 1.6;

        li {
          margin-bottom: 6px;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  .cancel-note {
    font-size: 0.85rem;
    color: var(--text-muted);
    margin-bottom: 24px;
    font-style: italic;
  }

  .cancel-actions {
    .keep-btn {
      padding: 14px 24px;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 10px;
      margin-bottom: 12px;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      }
    }

    .cancel-btn {
      color: var(--text-muted);
      font-size: 0.9rem;
      text-decoration: none;
      
      &:hover {
        color: #ef4444;
        text-decoration: none;
      }
    }
  }
}

.credit-topup-section {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  overflow: hidden;

  .credit-topup-header {
    padding: 20px 24px;
    border-bottom: 1px solid $dark-border2;

    .credit-topup-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 4px 0;
    }

    .credit-topup-subtitle {
      color: var(--text-secondary);
      font-size: 0.875rem;
      margin: 0;
    }
  }

  .credit-topup-card {
    padding: 24px;
  }
}

.manual-topup-section {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  overflow: hidden;

  .credit-topup-header {
    padding: 20px 24px;
    border-bottom: 1px solid $dark-border2;

    .credit-topup-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 4px 0;
    }

    .credit-topup-subtitle {
      color: var(--text-secondary);
      font-size: 0.875rem;
      margin: 0;
    }
  }

  .credit-topup-card {
    padding: 24px 24px 32px 24px;
  }

  .manual-topup-presets {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
}
</style>

<script>
import Input from "@/components/form/Input"
import Select from "@/components/form/Select"
import { Switch as cSwitch } from '@coreui/vue'
import PaymentInfo from './PaymentInfo'

export default {
    name: 'Subscription',
    components: {
        InputField: Input,
        SelectField: Select,
        cSwitch,
        PaymentInfo
    },
    props: {
        settings: {
            type: Object
        }
    },
    data: function() {
        return {
            tdsInProcess: false,
            canDisplaySubscription: false,
            isSystemInit: false,
            subModal: {
                show: false,
                data: {
                    account_uuid: '',
                    paymentMethod: "creditcard",
                    isAnnual: true
                },name: '',
                type: '',
                components: 0,
                base_price: 0,
                api_requests: null,
                media_storage: null,
                is_lifetime: false,
                is_private: false,
                is_subscription: false,
                paymentModal: false,
                creditcard: {
                    name: '',
                    number: '',
                    expires: '',
                    cvv: ''
                }
            },
            paymentInProcess: false,
            packages: [],
            summary: {
                total: null,
                discount: 0
            },
            paymentInfo: null,
            updateModal: {
                show: false,
                creditcard: {
                    name: '',
                    number: '',
                    expires: '',
                    cvv: ''
                }
            },
            cancelModal: {
                show: false,
                loading: false,
                subscriptionUuid: null,
                isTrialCancellation: false
            },
            creditSettings: {
                auto_topup_enabled: false,
                auto_topup_threshold: 0,
                auto_topup_amount: 0,
                currency: 'USD'
            },
            loadingCreditSettings: false,
            savingCreditSettings: false,
            billingCurrency: 'USD',
            exchangeRate: 1,
            topupBonus: 10,
            manualTopupPresets: [5, 10, 25, 50, 100],
            manualTopupAmount: 0,
            manualTopupCustom: false,
            manualTopupCustomAmount: null,
            doingManualTopup: false
        }
    },

    computed: {
        manualTopupEffectiveAmount() {
            if (this.manualTopupCustom) {
                return this.manualTopupCustomAmount > 0 ? this.manualTopupCustomAmount : 0
            }
            // Presets are in USD — convert to billing currency
            var base = this.manualTopupAmount > 0 ? this.manualTopupAmount : 0
            return this.exchangeRate !== 1 ? base * this.exchangeRate : base
        },

        subscriptions: {
            get() {
                return this.$store.getters.getSubscriptions
            },

            set(val) {
                this.$store.commit('SET_SUBSCRIPTIONS', val)
            }

        },

        allSubscriptions() {
            if (!this.subscriptions || this.subscriptions.length === 0) {
                return []
            }
            
            // Create a map to track which subscriptions have been updated with master status
            const updatedSubs = new Map()
            
            // First, group by ref_no and apply master status
            const grouped = {}
            this.subscriptions.filter(s => s.ref_no).forEach(sub => {
                if (!grouped[sub.ref_no]) {
                    grouped[sub.ref_no] = []
                }
                grouped[sub.ref_no].push(sub)
            })
            
            // Apply master subscription status to all items in each group
            Object.keys(grouped).forEach(refNo => {
                const group = grouped[refNo]
                const master = group.find(s => s.license_code)
                
                if (master) {
                    group.forEach(sub => {
                        const updated = {
                            ...sub,
                            is_expired: master.is_expired,
                            is_canceled: master.is_canceled,
                            is_active: master.is_active,
                            expires_at: master.expires_at,
                            CanceledAt: master.CanceledAt
                        }
                        updatedSubs.set(sub.uuid, updated)
                    })
                }
            })
            
            // Return all subscriptions with updated status where applicable
            return this.subscriptions.map(sub => {
                return updatedSubs.has(sub.uuid) ? updatedSubs.get(sub.uuid) : sub
            })
        },

        activeSubscriptions() {
            return this.allSubscriptions.filter(s => s.is_active && !s.is_expired)
        },

        expiredSubscriptions() {
            return this.allSubscriptions.filter(s => s.is_expired)
        },

        groupedSubscriptions() {
            if (!this.allSubscriptions || this.allSubscriptions.length === 0) {
                return {}
            }
            
            // Group by ref_no (subscriptions already have inherited master status from allSubscriptions)
            const grouped = {}
            
            this.allSubscriptions.filter(s => s.ref_no).forEach(sub => {
                if (!grouped[sub.ref_no]) {
                    grouped[sub.ref_no] = []
                }
                grouped[sub.ref_no].push(sub)
            })
            
            return grouped
        },

        isLifetime() {
            let pack = this.packages.find(p => {
               return p.value == this.subModal.data.account_uuid
           })

           if (pack !== undefined) {
                return pack.isLifetime
           }

           return false
        }
    },

    methods: {
        getGroupTitle(group) {
            const templateSub = group.find(s => s.license_product.group === 'Template')
            if (templateSub) {
                return `${templateSub.license_product.name} - ${this.$t('subscription.templateBundle')}`
            }
            return `${group.length} ${this.$t('subscription.items')} - ${this.$t('subscription.sharedLicense')}`
        },

        getGroupTotalPrice(group) {
            const total = group.reduce((sum, sub) => sum + sub.human_price, 0)
            return total.toFixed(2)
        },

        getGroupLicenseCode(group) {
            // Find the subscription with a license_code (usually the template)
            const subWithLicense = group.find(s => s.license_code)
            return subWithLicense ? subWithLicense.license_code : null
        },

        getGroupBundleProducts(group) {
            // Return all products except the main one (the one with license_code)
            // This will show integrations and apps that are part of the template bundle
            return group
                .filter(s => !s.license_code || s.license_product.group !== 'Template')
                .map(s => s.license_product)
        },

        getProductBadgeVariant(group) {
            const variants = {
                'Integration': 'primary',
                'App': 'success',
                'Template': 'info'
            }
            return variants[group] || 'secondary'
        },

        getProductIcon(group) {
            const icons = {
                'Integration': 'plug',
                'Template': 'file-earmark-code',
                'App': 'grid-3x3-gap'
            }
            return icons[group] || 'box'
        },

        getStatusVariant(subscription) {
            if (subscription.is_canceled) return 'danger'
            if (subscription.is_expired) return 'warning'
            if (subscription.is_paused) return 'secondary'
            if (subscription.is_active) return 'success'
            return 'secondary'
        },

        getStatusText(subscription) {
            if (subscription.is_canceled) return this.$t('subscription.canceled')
            if (subscription.is_expired) return this.$t('expired')
            if (subscription.is_paused) return this.$t('subscription.paused')
            if (subscription.is_active) return this.$t('active')
            return this.$t('inactive')
        },

        getPaymentIcon(method) {
            const icons = {
                'card': 'credit-card',
                'creditcard': 'credit-card',
                'prepayment': 'bank',
                'invoice': 'receipt'
            }
            return icons[method] || 'wallet2'
        },

        formatPaymentMethod(method) {
            const methods = {
                'card': this.$t('subscription.card'),
                'creditcard': this.$t('subscription.card'),
                'prepayment': this.$t('subscription.prepayment'),
                'invoice': this.$t('subscription.invoice')
            }
            return methods[method] || method
        },

        getPaymentStatusVariant(status) {
            const variants = {
                'completed': 'success',
                'pending': 'warning',
                'failed': 'danger',
                'pending_approval': 'info'
            }
            return variants[status] || 'secondary'
        },

        formatPaymentStatus(status) {
            const statuses = {
                'completed': this.$t('subscription.completed'),
                'pending': this.$t('subscription.pending'),
                'failed': this.$t('subscription.failed'),
                'pending_approval': this.$t('subscription.pendingApproval')
            }
            return statuses[status] || status
        },

        formatDate(dateStr) {
            if (!dateStr) return '-'
            const date = new Date(dateStr)
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },

        isExpiringSoon(subscription) {
            if (!subscription.expires_at) return false
            const expiresAt = new Date(subscription.expires_at)
            const now = new Date()
            const daysUntilExpiry = (expiresAt - now) / (1000 * 60 * 60 * 24)
            return daysUntilExpiry <= 14 && daysUntilExpiry > 0
        },

        copyLicenseCode(code) {
            navigator.clipboard.writeText(code)
            this.$bvToast.toast(this.$t('savedToClipboard'), {
                variant: 'success',
                solid: true,
                autoHideDelay: 2000
            })
        },

        showActions(subscription) {
            return this.canCancel(subscription) || this.canRenew(subscription) || this.canRemove(subscription)
        },

        formatTotalPrice() {
            if (!this.subscriptions || this.subscriptions.length === 0) return '$0'
            
            const total = this.subscriptions.reduce((sum, sub) => {
                return sum + (sub.human_price || 0)
            }, 0)

            const currency = this.subscriptions[0].currency
            if (currency === 'USD') {
                return '$' + total.toFixed(2)
            }
            return total.toFixed(2) + ' ' + currency
        },

        proceed() {
            this.subModal.paymentModal = true

            let isAnnual = this.subModal.data.isAnnual;
            if (this.isLifetime) {
                isAnnual = false
            }

            this.$transactionApi.post("/calculate_price", {uuid: this.subModal.data.account_uuid, is_annual: isAnnual}).then(resp => {
                this.summary.total = resp.data.price / 100
                this.summary.discount = resp.data.discount
            }).catch(() => {
                 this.$mainDialog.msgBoxOk(this.$t('somethingUnexpectedHappened'));
                 this.subModal.paymentModal = false
            })
        },

        cancel(uuid) {
            const subscription = this.allSubscriptions.find(s => s.uuid === uuid)
            this.cancelModal.subscriptionUuid = uuid
            this.cancelModal.isTrialCancellation = subscription ? this.isInTrialPeriod(subscription) : false
            this.cancelModal.show = true
        },

        confirmCancel() {
            this.cancelModal.loading = true
            this.$transactionApi.post("/subscription/cancel", {
                subscription_id: this.cancelModal.subscriptionUuid
            }).then(() => {
                this.$store.dispatch("fetchSubscriptions")
                this.cancelModal.show = false
                this.$bvToast.toast(this.$t('subscription.cancelModal.success'), {
                    variant: 'success',
                    solid: true
                })
            }).catch(() => {
                this.$mainDialog.msgBoxOk(this.$t('somethingUnexpectedHappened'));
            }).finally(() => {
                this.cancelModal.loading = false
            })
        },

        activate(licenseCode) {
            this.$transactionApi.post("/subscription/enable/" + licenseCode).then(() => {
                this.$store.dispatch("fetchSubscriptions")
            }).catch(() => {
                 this.$mainDialog.msgBoxOk(this.$t('somethingUnexpectedHappened'));
            })
        },

        renew(uuid) {
            const subscription = this.allSubscriptions.find(s => s.uuid === uuid)
            
            if (!subscription) {
                this.$mainDialog.msgBoxOk(this.$t('somethingUnexpectedHappened'))
                return
            }

            this.$mainDialog.msgBoxConfirm(
                this.$t('subscription.renewConfirmMessage') || 'Are you sure you want to renew this subscription?',
                {
                    title: this.$t('subscription.renewConfirmTitle') || 'Renew Subscription',
                    okVariant: 'success',
                    okTitle: this.$t('yes') || 'Yes',
                    cancelTitle: this.$t('no') || 'No',
                    centered: true
                }
            ).then(confirmed => {
                if (!confirmed) return
                
                const popup = this.$loadingPopup()
                
                // Get product type and reference from subscription
                const productType = subscription.license_product.group.toLowerCase()
                const reference = subscription.license_product.product_reference || subscription.license_product.id
                
                this.$transactionApi.post("/checkout/link", {
                    product_type: productType,
                    reference: reference,
                    currency: subscription.currency,
                    lang: this.$store.getters.getLocale,
                    source: `subscription-renew:${productType}:${reference}`
                }).then(resp => {
                    window.open(resp.data, '_blank')
                }).catch(() => {
                    this.$mainDialog.msgBoxOk(this.$t('marketplace.failedToCheckout') || 'Failed to create checkout link')
                }).finally(() => {
                    popup.hide()
                })
            }).catch(() => {
                // User cancelled
            })
        },

        remove(uuid) {
            this.$mainDialog.msgBoxConfirm(
                this.$t('subscription.removeConfirmMessage') || 'Are you sure you want to permanently remove this subscription? This action cannot be undone.',
                {
                    title: this.$t('subscription.removeConfirmTitle') || 'Remove Subscription',
                    okVariant: 'danger',
                    okTitle: this.$t('yes') || 'Yes',
                    cancelTitle: this.$t('no') || 'No',
                    centered: true
                }
            ).then(confirmed => {
                if (!confirmed) return
                
                this.$transactionApi.post("/subscription/remove", {subscription_id: uuid}).then(() => {
                    this.$store.dispatch("fetchSubscriptions")
                    this.$bvToast.toast(this.$t('subscription.removeSuccess') || 'Subscription removed successfully', {
                        variant: 'success',
                        solid: true
                    })
                }).catch(() => {
                    this.$mainDialog.msgBoxOk(this.$t('somethingUnexpectedHappened'));
                })
            }).catch(() => {
                // User cancelled
            })
        },

        systemInitModal() {
            this.subModal.show = true
            this.isSystemInit = true

            if (this.subscription.period != 12) {
                this.subModal.data.isAnnual = false
            }

            this.subModal.data.account_uuid = this.subscription.account_uuid
        },

        subscribe(e) {
            e.preventDefault();

            this.paymentInProcess = true;

            var data = {
                account_uuid: this.subModal.data.account_uuid,
                payment_method: this.subModal.data.paymentMethod,
                is_annual: this.subModal.data.isAnnual,
                payment_info: {}
            }

            if (this.subModal.data.paymentMethod == 'creditcard') {
                data.payment_info.creditcard = this.subModal.creditcard
            }

            this.$transactionApi.post("/billing/subscription", data).then(resp => {
                this.subscription = resp.data
                this.paymentInProcess = false

                this.$emit("subscribed", this.subscription)

                this.$mainDialog.msgBoxOk(this.$t('purchaseSuccessful'));
                this.subModal.show = false;
            }).catch(err => {
                this.paymentInProcess = false

                if (err.response !== undefined) {
                    if (err.response.status == 406) {
                        this.$mainDialog.msgBoxOk(this.$t('failedOrder'))
                        return
                    }

                    // this means payment is 3ds and we need to redirect user to another page
                    if (err.response.status == 308) {
                        let data = err.response.data
                        let params = new URLSearchParams(data.Params)
                        let href = data.Href + "?" + params

                        window.open(href, "_blank")

                        this.tdsInProcess = true
                        this.paymentInProcess = true

                        // create pooling to detect what happened to subscription
                        let waitingTime = 0
                        let checkStatus = setInterval(() => {
                            this.$store.dispatch("fetchSubscriptions").then(resp => {
                                if (resp.payment_status !== "failed") {
                                    this.tdsInProcess = false;
                                    this.paymentInProcess = false;

                                    clearInterval(checkStatus)
                                }
                            })

                            if (waitingTime >= 10) {
                                clearInterval(checkStatus)
                                this.tdsInProcess = false;
                                this.paymentInProcess = false;

                                this.subModal.paymentModal = false;
                            }

                            waitingTime++
                        }, 3000)

                        return
                    }
                }

                this.$mainDialog.msgBoxOk(this.$t('somethingUnexpectedHappened'))
            })
        },

        getChargeTotal() {
           let pack = this.packages.find(p => {
               return p.value == this.subModal.data.account_uuid
           })

           if (pack !== undefined) {
                return pack.basePrice
           }

           return "0"
        },

        canCancel(subscription) {
            return subscription.is_active && !subscription.is_canceled
        },

        canRenew(subscription) {
            return subscription.is_expired && !subscription.is_canceled
        },

        canRemove(subscription) {
            return subscription.is_expired || subscription.is_canceled
        },

        isExpired(subscription) {
            return subscription.is_expired
        },

        isInTrialPeriod(subscription) {
            if (!subscription.trial_period_days || subscription.trial_period_days === 0) {
                return false
            }
            
            const createdAt = new Date(subscription.CreatedAt)
            const now = new Date()
            const daysSinceCreated = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24))
            
            return daysSinceCreated < subscription.trial_period_days
        },

        getRemainingTrialDays(subscription) {
            if (!subscription.trial_period_days) {
                return 0
            }
            
            const createdAt = new Date(subscription.CreatedAt)
            const now = new Date()
            const daysSinceCreated = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24))
            const remainingDays = subscription.trial_period_days - daysSinceCreated
            
            return Math.max(0, remainingDays)
        },

        loadCreditSettings() {
            this.loadingCreditSettings = true
            this.$transactionApi.get('/billing/credit-settings').then(resp => {
                if (resp && resp.data) {
                    this.creditSettings.auto_topup_enabled = resp.data.auto_topup_enabled || false
                    this.creditSettings.auto_topup_threshold = (resp.data.auto_topup_threshold || 0) / 100
                    this.creditSettings.auto_topup_amount = (resp.data.auto_topup_amount || 0) / 100
                    this.creditSettings.currency = resp.data.currency || 'USD'
                }
            }).catch(err => {
                console.error('Failed to load credit settings', err)
            }).finally(() => {
                this.loadingCreditSettings = false
            })
        },

        saveCreditSettings() {
            this.savingCreditSettings = true
            var payload = {
                auto_topup_enabled: this.creditSettings.auto_topup_enabled,
                auto_topup_threshold: Math.round(this.creditSettings.auto_topup_threshold * 100),
                auto_topup_amount: Math.round(this.creditSettings.auto_topup_amount * 100),
                currency: this.creditSettings.currency
            }
            this.$transactionApi.put('/billing/credit-settings', payload).then(() => {
                this.$bvToast.toast(this.$t('creditTopup.saveSuccess'), {
                    variant: 'success',
                    solid: true
                })
            }).catch(err => {
                console.error('Failed to save credit settings', err)
                this.$bvToast.toast(this.$t('creditTopup.saveError'), {
                    variant: 'danger',
                    solid: true
                })
            }).finally(() => {
                this.savingCreditSettings = false
            })
        },

        selectTopupPreset(amount) {
            this.manualTopupAmount = amount
            this.manualTopupCustom = false
            this.manualTopupCustomAmount = null
        },

        formatPreset: function(preset) {
            // Presets are in USD — convert to billing currency for display
            var display = this.exchangeRate !== 1 ? preset * this.exchangeRate : preset
            return this.formatMoney(display)
        },

        formatMoney: function(amount) {
            // Amount is already in the billing currency — just format
            if (this.billingCurrency === 'USD') {
                return '$' + amount.toFixed(2)
            }
            return amount.toFixed(2) + ' ' + this.billingCurrency
        },

        fetchBillingCurrency: function() {
            var self = this
            this.$transactionApi.get('/billing/balance').then(function(resp) {
                if (resp && resp.data) {
                    if (resp.data.currency) {
                        self.billingCurrency = resp.data.currency
                    }
                    if (resp.data.exchange_rate && resp.data.exchange_rate > 0) {
                        self.exchangeRate = resp.data.exchange_rate
                    } else if (resp.data.balance_usd && resp.data.balance_display && resp.data.balance_usd > 0) {
                        self.exchangeRate = resp.data.balance_display / resp.data.balance_usd
                    }
                    if (resp.data.topup_bonus != null) {
                        self.topupBonus = resp.data.topup_bonus
                    }
                }
            }).catch(function() {})
        },

        doManualTopup() {
            // Compute USD cents: presets are already in USD, custom amounts are in billing currency
            var usdCents
            if (this.manualTopupCustom) {
                usdCents = Math.round(this.manualTopupCustomAmount / this.exchangeRate * 100)
            } else {
                usdCents = this.manualTopupAmount * 100
            }
            if (usdCents <= 0) return

            this.doingManualTopup = true
            this.$transactionApi.post('/billing/topup', {
                amount_cents: usdCents,
                currency: this.billingCurrency
            }).then(resp => {
                if (resp && resp.data && resp.data.checkout_url) {
                    window.location.href = resp.data.checkout_url
                }
            }).catch(err => {
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : this.$t('creditTopup.manualError')
                this.$bvToast.toast(msg, {
                    variant: 'danger',
                    solid: true
                })
            }).finally(() => {
                this.doingManualTopup = false
            })
        }
    },

    mounted() {
        this.$http.get("/v1/user/register/accounts").then(resp => {
            this.packages = resp.data.filter(d => {
                return d.is_subscription
            }).map(d => {
                return {
                    text: d.name,
                    value: d.uuid,
                    basePrice: (d.base_price / 100).toString(),
                    isLifetime: d.is_lifetime,
                }
            })


        }).catch(err => {
            console.log(err)
        })

        this.$store.dispatch("fetchSubscriptions")
        this.loadCreditSettings()
        this.fetchBillingCurrency()
    }
}
</script>
